import * as React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { GearIcon } from "@phosphor-icons/react/dist/ssr/Gear";

import {
	usePlatformSettings,
	useUpdatePlatformSettings,
	type PlatformSetting,
} from "@/hooks/api/use-platform-settings";
import { useDialog } from "@/hooks/use-dialog";
import { ConfirmModal } from "@/components/confirm-modal/confirm-modal";

import { AgentTemplatesSection } from "./agent-templates-section";
import {
	AGENT_TEMPLATE_GROUP,
	buildUpdates,
	KNOWN_GROUP_ORDER,
	SECTION_META,
	VOICE_OPTIONS_KEY,
	VOICES_GROUP,
	WORKSPACE_GROUP,
} from "./lib";
import { SaveBar } from "./save-bar";
import { SettingRow } from "./setting-row";
import { VoicesSection } from "./voices-section";

interface Section {
	name: string;
	items: PlatformSetting[];
}

export function PlatformSettings(): React.JSX.Element {
	const { data, isLoading, error } = usePlatformSettings();
	const updateMutation = useUpdatePlatformSettings();

	// Drafts keyed by setting key, across ALL sections — switching sections
	// keeps pending edits, and one save bar flushes everything at once.
	const [drafts, setDrafts] = React.useState<Record<string, string>>({});
	const [activeGroup, setActiveGroup] = React.useState<string | null>(null);

	const {
		open: isClearModalOpen,
		data: clearTarget,
		handleOpen: handleClearModalOpen,
		handleClose: handleClearModalClose,
	} = useDialog<PlatformSetting>();

	const items = React.useMemo(() => data ?? [], [data]);

	// Sections come from the groups the API actually returns: known groups in a
	// fixed order, anything else appended generically at the end.
	const sections: Section[] = React.useMemo(() => {
		const names = [...new Set(items.map((item) => item.group))];
		names.sort((a, b) => {
			const indexA = KNOWN_GROUP_ORDER.indexOf(a);
			const indexB = KNOWN_GROUP_ORDER.indexOf(b);
			return (
				(indexA === -1 ? KNOWN_GROUP_ORDER.length : indexA) - (indexB === -1 ? KNOWN_GROUP_ORDER.length : indexB)
			);
		});
		return names.map((name) => ({ name, items: items.filter((item) => item.group === name) }));
	}, [items]);

	const currentGroup = sections.some((section) => section.name === activeGroup)
		? (activeGroup as string)
		: (sections[0]?.name ?? null);
	const currentSection = sections.find((section) => section.name === currentGroup) ?? null;

	// The exact PUT payload for the current drafts — its length drives the save
	// bar, the per-row "Edited" chips, and the per-section nav badges.
	const changes = React.useMemo(() => buildUpdates(items, drafts), [items, drafts]);
	const dirtyKeys = React.useMemo(() => new Set(changes.map((change) => change.key)), [changes]);
	const dirtyCountByGroup = React.useMemo(() => {
		const counts: Record<string, number> = {};
		for (const item of items) {
			if (dirtyKeys.has(item.key)) {
				counts[item.group] = (counts[item.group] ?? 0) + 1;
			}
		}
		return counts;
	}, [items, dirtyKeys]);

	const setDraft = (key: string, value: string) => {
		setDrafts((previous) => ({ ...previous, [key]: value }));
	};

	const handleDiscard = () => {
		setDrafts({});
	};

	const handleSave = async () => {
		if (changes.length === 0) {
			return;
		}
		try {
			await updateMutation.mutateAsync({ settings: changes });
			setDrafts({});
		} catch {
			// Errors are toasted by the mutation hook; drafts stay for a retry.
		}
	};

	const handleClearOverride = async () => {
		if (!clearTarget) {
			return;
		}

		await updateMutation.mutateAsync({ settings: [{ key: clearTarget.key, value: null }] });
		setDrafts((previous) => {
			const next = { ...previous };
			delete next[clearTarget.key];
			return next;
		});
	};

	const renderSection = (section: Section): React.JSX.Element => {
		const meta = SECTION_META[section.name];

		return (
			<Card>
				<CardContent sx={{ p: 3 }}>
					<Stack spacing={2}>
						<Box>
							<Typography variant="h6">{meta?.title ?? section.name}</Typography>
							{meta ? (
								<Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
									{meta.description}
								</Typography>
							) : null}
						</Box>

						{section.name === AGENT_TEMPLATE_GROUP ? (
							<AgentTemplatesSection
								disabled={updateMutation.isPending}
								drafts={drafts}
								items={section.items}
								onChange={setDraft}
							/>
						) : section.name === VOICES_GROUP && section.items.some((item) => item.key === VOICE_OPTIONS_KEY) ? (
							<Stack spacing={2}>
								<VoicesSection
									disabled={updateMutation.isPending}
									draft={drafts[VOICE_OPTIONS_KEY]}
									item={section.items.find((item) => item.key === VOICE_OPTIONS_KEY) as PlatformSetting}
									onChange={setDraft}
								/>
								{section.items.some((item) => item.key !== VOICE_OPTIONS_KEY) ? (
									<Stack divider={<Divider />}>
										{section.items
											.filter((item) => item.key !== VOICE_OPTIONS_KEY)
											.map((item) => (
												<SettingRow
													disabled={updateMutation.isPending}
													dirty={dirtyKeys.has(item.key)}
													draft={drafts[item.key]}
													item={item}
													key={item.key}
													onChange={setDraft}
													onClearOverride={handleClearModalOpen}
												/>
											))}
									</Stack>
								) : null}
							</Stack>
						) : (
							<Stack divider={<Divider />}>
								{section.name === WORKSPACE_GROUP ? (
									<Typography color="text.secondary" variant="body2" sx={{ pb: 2 }}>
										Every template agent, shared connection, and voice is managed inside this workspace like any
										other account — use the account switcher to open it. Its account id is how the platform
										finds it, and is written automatically the first time the workspace is provisioned.
									</Typography>
								) : null}
								{section.items.map((item) => (
									<SettingRow
										disabled={updateMutation.isPending}
										dirty={dirtyKeys.has(item.key)}
										draft={drafts[item.key]}
										item={item}
										key={item.key}
										onChange={setDraft}
										onClearOverride={handleClearModalOpen}
									/>
								))}
							</Stack>
						)}
					</Stack>
				</CardContent>
			</Card>
		);
	};

	return (
		<Box
			sx={{
				maxWidth: "var(--Content-maxWidth)",
				m: "var(--Content-margin)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Stack spacing={4}>
				<Box>
					<Typography variant="h4">Platform settings</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						Keys, templates, and voices behind the self-service platform. Values set here override environment
						configuration.
					</Typography>
				</Box>

				{isLoading ? (
					<Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
						<CircularProgress size={30} />
					</Box>
				) : error ? (
					<Alert severity="error">Failed to load platform settings. Please try again.</Alert>
				) : data === null ? (
					<Alert severity="info">Platform settings API not available</Alert>
				) : sections.length === 0 ? (
					<Alert severity="info">No platform settings to manage yet.</Alert>
				) : (
					<Box sx={{ alignItems: "flex-start", display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4 }}>
						{/* Mobile: section picker on top. */}
						<FormControl fullWidth size="small" sx={{ display: { xs: "block", md: "none" } }}>
							<Select
								fullWidth
								onChange={(event) => setActiveGroup(event.target.value)}
								value={currentGroup ?? ""}
							>
								{sections.map((section) => {
									const dirtyCount = dirtyCountByGroup[section.name] ?? 0;
									return (
										<MenuItem key={section.name} value={section.name}>
											{SECTION_META[section.name]?.title ?? section.name}
											{dirtyCount > 0 ? ` • ${dirtyCount} unsaved` : ""}
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>

						{/* Desktop: sticky section nav. */}
						<Box
							component="nav"
							sx={{
								display: { xs: "none", md: "block" },
								flexShrink: 0,
								position: "sticky",
								top: 24,
								width: 220,
							}}
						>
							<List disablePadding>
								{sections.map((section) => {
									const meta = SECTION_META[section.name];
									const NavIcon = meta?.icon ?? GearIcon;
									const dirtyCount = dirtyCountByGroup[section.name] ?? 0;

									return (
										<ListItemButton
											key={section.name}
											onClick={() => setActiveGroup(section.name)}
											selected={section.name === currentGroup}
											sx={{ borderRadius: 1, mb: 0.5 }}
										>
											<ListItemIcon sx={{ minWidth: 36 }}>
												<NavIcon fontSize="var(--Icon-fontSize)" />
											</ListItemIcon>
											<ListItemText
												primary={meta?.title ?? section.name}
												primaryTypographyProps={{ noWrap: true, variant: "subtitle2" }}
											/>
											{dirtyCount > 0 ? (
												<Chip color="warning" label={dirtyCount} size="small" variant="outlined" />
											) : null}
										</ListItemButton>
									);
								})}
							</List>
						</Box>

						{/* Active section content. */}
						<Box sx={{ flex: "1 1 auto", minWidth: 0, width: { xs: "100%", md: "auto" } }}>
							{currentSection ? renderSection(currentSection) : null}
						</Box>
					</Box>
				)}

				<SaveBar
					count={changes.length}
					onDiscard={handleDiscard}
					onSave={handleSave}
					saving={updateMutation.isPending}
				/>

				<ConfirmModal
					open={isClearModalOpen}
					onClose={handleClearModalClose}
					onSumbit={handleClearOverride}
					title="Clear override?"
					description={`Are you sure you want to clear the panel override for "${clearTarget?.label ?? ""}"? The value will fall back to the environment configuration.`}
				/>
			</Stack>
		</Box>
	);
}
